import React, { useState, useEffect } from 'react';

/**
 * TemperatureBadge — small stacked °C / °F readout for the top-left corner.
 *
 * Uses Open-Meteo (https://open-meteo.com) — free, no API key required,
 * CORS-friendly. Updates every 15 minutes.
 *
 * Renders nothing while loading or on error to keep the header clean.
 */
const TemperatureBadge = ({ location, textColors }) => {
  const [temp, setTemp] = useState(null);  // {c: number, f: number}

  useEffect(() => {
    if (!location?.lat || !location?.lon) return;

    let cancelled = false;
    const fetchWeather = async () => {
      try {
        const url =
          `https://api.open-meteo.com/v1/forecast` +
          `?latitude=${location.lat}&longitude=${location.lon}` +
          `&current=temperature_2m&temperature_unit=celsius`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        const c = data?.current?.temperature_2m;
        if (typeof c !== 'number' || cancelled) return;
        const f = c * 9 / 5 + 32;
        setTemp({ c: Math.round(c), f: Math.round(f) });
      } catch (_) { /* silent fail — badge simply stays hidden */ }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000); // 15 min
    return () => { cancelled = true; clearInterval(interval); };
  }, [location?.lat, location?.lon]);

  if (!temp) return null;

  return (
    <div
      className="flex flex-col items-center justify-center px-2 py-1 rounded-lg leading-none"
      style={{
        background: 'rgba(255, 255, 255, 0.08)',
        border: `1px solid ${textColors.primary === '#FFFFFF' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}`,
        minWidth: '46px',
      }}
      data-testid="temperature-badge"
      title={`${temp.c}°C / ${temp.f}°F`}
    >
      <span
        className="tabular-nums font-medium"
        style={{ color: textColors.primary, fontSize: '11px', lineHeight: 1.15 }}
        data-testid="temp-c"
      >
        {temp.c}°C
      </span>
      <span
        className="tabular-nums font-medium"
        style={{ color: textColors.secondary, fontSize: '10px', lineHeight: 1.15, marginTop: '1px' }}
        data-testid="temp-f"
      >
        {temp.f}°F
      </span>
    </div>
  );
};

export default TemperatureBadge;
